import * as Api from '@ViewModels';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { reactQueryClient } from '../../web/ReactQueryProvider';

const INFINITE_CUSTOM_SURVEYS_QUERY_KEY = 'survey/custom';

export function useInfiniteCustomSurveys({
	shouldIncludeArchived = false,
	shouldIncludeExpired = false,
	search = '',
	pageSize = 100,
	enabled = true,
	hasAiAgents,
	impersonationContext,
}: {
	shouldIncludeArchived?: boolean;
	shouldIncludeExpired?: boolean;
	search?: string;
	pageSize?: number;
	enabled?: boolean;
	/** When true, only surveys linked to an AI agent are returned. When false, only surveys with no AI agents are returned. When omitted, all surveys are returned. */
	hasAiAgents?: boolean;
	impersonationContext?: Api.IImpersonationContext;
	transformer?: (page: Api.IPagedCollection<Api.ICustomSurvey>) => Api.IPagedCollection<Api.ICustomSurvey>;
}) {
	const userSession = useUserSession();
	return useInfiniteQuery<Api.IPagedCollection<Api.ICustomSurvey>>({
		enabled,
		getNextPageParam: lastPage => lastPage.pageToken,
		queryFn: async ({ pageParam }) => {
			const page = await userSession.webServiceHelper.callAsync<Api.IPagedCollection<Api.ICustomSurvey>>(
				Api.ImpersonationBroker.composeApiUrl({
					impersonationContext,
					queryParams: {
						includeArchived: shouldIncludeArchived,
						includeExpired: shouldIncludeExpired,
						search,
						pageSize,
						pageToken: pageParam,
						// Only request aiAgents expansion when the results may include AI surveys
						...(hasAiAgents === true ? { expand: 'aiAgents' } : {}),
						...(hasAiAgents !== undefined ? { hasAiAgents } : {}),
					},
					urlPath: `survey/custom`,
				}),
				'GET',
				null
			);

			return page;
		},
		queryKey: [
			INFINITE_CUSTOM_SURVEYS_QUERY_KEY,
			shouldIncludeArchived,
			shouldIncludeExpired,
			search,
			pageSize,
			enabled,
			hasAiAgents,
			impersonationContext,
		],
		initialPageParam: '',
	});
}

export const invalidateInfiniteCustomSurveys = () => {
	reactQueryClient.invalidateQueries({ queryKey: [INFINITE_CUSTOM_SURVEYS_QUERY_KEY] });
};
